<template>
  <div class="service-categories">
    <div class="service-categories__grid">
      <div class="service-categories__breadcrumbs">
        <template v-if="selectedItemName">
          <NuxtLink :to="getRootBreadcrumbLink()" class="service-categories__breadcrumbs-root service-categories__breadcrumbs-root--clickable">Все услуги</NuxtLink>
        </template>
        <template v-else>
          <span class="service-categories__breadcrumbs-root">Все услуги</span>
        </template>
        <template v-if="selectedItemName">
          <span class="service-categories__breadcrumbs-separator">›</span>
          <span class="service-categories__breadcrumbs-current">{{ selectedItemName }}</span>
        </template>
      </div>
      <div class="service-categories__list">
        <component
            :is="inert ? 'button' : NuxtLink"
            v-for="item in displayItems"
            :key="`category-${item.id}`"
            :type="inert ? 'button' : undefined"
            :to="inert ? undefined : getItemLink(item.slug)"
            :aria-pressed="inert ? String(!!item.selected) : undefined"
            @click="inert ? emit('select', item.slug) : undefined"
            class="service-categories__item"
            :class="{
              'service-categories__item--disabled': item.disabled,
              'service-categories__item--selected': item.selected
            }"
        >
          <span class="service-categories__item-text">{{ item.name }}</span>
          <span v-if="item.clinicsCount !== undefined" class="service-categories__item-count">{{ item.clinicsCount }} {{ getPlural(item.clinicsCount, ['клиника', 'клиники', 'клиник']) }}</span>
        </component>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Компонентом, а не строкой: строковый `:is` резолвится только по глобальной регистрации,
// а авто-импортированный NuxtLink по имени не находится и уезжает в вёрстку литеральным тегом.
import {NuxtLink} from "#components";
import {getPlural} from "~/utils/helpers";
import type {TClinicFilter} from "~/types/clinic";

type ServiceListItem = {
  id: number;
  name: string;
  slug: string;
  disabled?: boolean;
  selected?: boolean;
  clinicsCount?: number;
}

interface Props {
  items: ServiceListItem[];
  filters: TClinicFilter;
  currentName?: string;
  /** Каталог приходит в порядке спроса из спеки — алфавит его уничтожает. */
  keepOrder?: boolean;
  /** Строки прайса: собственного URL у них больше нет, поэтому это кнопки фильтра, а не ссылки. */
  inert?: boolean;
}

const emit = defineEmits<{ select: [slug: string] }>();

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
});

const displayItems = computed(() => {
  if (props.keepOrder) {
    return [...props.items];
  }

  return [...props.items].sort((a, b) => a.name.localeCompare(b.name));
});

const selectedItemName = computed(() => {
  if (props.currentName) {
    return props.currentName;
  }

  return '';
});

// Только плоский URL: города и вида в адресе каталога нет, а `/uslugi/{город}/{слаг}-{вид}/`
// после переезда отдаёт 301 — ссылаться на источник редиректа нельзя.
const getItemLink = (slug: string) => `/uslugi/${slug}/`;

const getRootBreadcrumbLink = () => {
  if (props.filters.city) {
    return `/uslugi/${props.filters.city}/`;
  } else {
    return `/uslugi/`;
  }
}

</script>

<style lang="scss">
.service-categories {
  $block: &;
  margin-bottom: 32px;

  &__grid {
    background: $color-white;
    border-radius: 32px;
    padding: 24px 40px 16px;
    display: flex;
    flex-direction: column;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px 12px;
  }

  &__breadcrumbs {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    padding: 0 12px;
    min-height: 19px;
    margin-bottom: 16px;

    &-root {
      font-size: 14px;
      line-height: 1.35;
      font-weight: 500;
      color: $color-black;

      &--clickable {
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &-current {
      color: $color-black;
      font-size: 14px;
      line-height: 1.35;
      font-weight: 500;
    }

    &-separator {
      color: $color-black;
      font-size: 14px;
      line-height: 1.35;
      margin: 0 4px;
    }
  }

  &__item {
    // Строки прайса рендерятся кнопкой — сбрасываем её собственную отрисовку,
    // чтобы фильтр и ссылка выглядели одинаково.
    background: none;
    border: 0;
    font: inherit;
    text-align: left;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border-radius: 8px;

    &-text {
      color: var(--black, $color-black);
      word-break: break-word;
      line-height: 1.35;
      font-size: 14px;
      font-weight: 500;
      transition: color 0.2s ease;
    }

    &-count {
      color: $color-gray;
      line-height: 1.35;
      font-size: 12px;
    }

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    &--selected:not(&--disabled) {
      background-color: $color-dark-white;
    }

    &:hover:not(&--disabled) {
      background-color: $color-dark-white;

      #{$block}__item-text {
        color: $color-accent-text;
      }
    }
  }

  @include media(lg) {
    &__grid {
      padding: 20px 24px 12px;
    }

    &__breadcrumbs {
      padding: 0 4px;
      margin-bottom: 12px;
    }
  }

  @include media(md) {
    &__list {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @include media(sm) {
    &__grid {
      padding: 12px 12px 8px;
      border-radius: 24px;
    }

    &__breadcrumbs {
      margin-bottom: 8px;
      padding: 0 2px;
    }

    &__list {
      grid-template-columns: repeat(2, 1fr);
      gap: 0 8px;
    }

    &__item {
      padding: 8px 4px;
      border-radius: 6px;
    }
  }
}
</style>
